import moment from 'moment';

export function AvailabilitiesFactory(restaurantInfoFactory, calloutService, $q, ORDER_TYPES){
    'ngInject';
    var DISCRETE_TIME_INTERVAL = 15; // time interval for displaying dates
    var SERVICE_DIVIDER = '----------------'; // divides service periods in the list of times
    var CLOSE_OUT = 4;
    var DAYSOFWEEK = [
        'Monday',
        'Tuesday',
        'Wednesday',
        'Thursday',
        'Friday',
        'Saturday',
        'Sunday'
    ];

    var initialDateTime,
        timeZone,
        availabilitySchedules;

    var setInitialDateTime = function(dateString){
        initialDateTime = dateString;
    };
    var getInitialDateTime = function(){
        return initialDateTime;
    };

    var setTimeZone = function(timeZoneString){
        timeZone = timeZoneString;
    };
    var getTimeZone = function(){
        return timeZone;
    };

    var getOperationHours = function() {
        let today = moment.parseZone(getInitialDateTime());
        return $q.all([
            isRestaurantOpenOnDay(ORDER_TYPES.DELIVERY, today),
            isRestaurantOpenOnDay(ORDER_TYPES.TAKE_OUT, today)
        ]).then(function([delivery, takeout]) {
            if(delivery && takeout) {
                return $q.all([
                    getStartEndTimes(today, ORDER_TYPES.DELIVERY),
                    getStartEndTimes(today, ORDER_TYPES.TAKE_OUT)
                ]).then(function([delivStartEnd, takeoutStartEnd]) {
                    return getOperationHoursFromSchedule(delivStartEnd, takeoutStartEnd);
                });
            } else if(delivery) {
                return getStartEndTimes(today, ORDER_TYPES.DELIVERY).then(function(delivStartEnd) {
                    return getOperationHoursFromSchedule(delivStartEnd, []);
                });
            } else if (takeout) {
                return getStartEndTimes(today, ORDER_TYPES.TAKE_OUT).then(function(takeoutStartEnd) {
                    return getOperationHoursFromSchedule([], takeoutStartEnd);
                });
            } else {
                return '';
            }

        });
    };
    function getOperationHoursFromSchedule(delivery, takeout) {
        let startTimes = [];
        let finishTimes = [];
        if (delivery.length) {
            startTimes.push(delivery[0].startTime);
            finishTimes.push(delivery[delivery.length - 1].endTime);
        }
        if (takeout.length){
            startTimes.push(takeout[0].startTime);
            finishTimes.push(takeout[takeout.length - 1].endTime);
        }
        if (startTimes.length && finishTimes.length) {
            const startTime = moment.min(startTimes);
            const endTime = moment.max(finishTimes);
            return `${formatTime(startTime)} - ${formatTime(endTime)}`;
        }
        return '';
    }

    function formatTime(time) {
        return time.format('h:mm A');
    }

    function _setAvailabilitySchedules(schedules, diningOption, availabilitySchedules) {
        for (var i = 0; i < schedules.length; i += 1) {
            var date = moment.parseZone(getInitialDateTime());
            var todaySchedules = schedules[i];
            var yesterdaySchedules = schedules[(i - 1 + schedules.length * 2) % schedules.length];
            var servicePeriods = _scheduleToServicePeriod(yesterdaySchedules, todaySchedules, date);
            if (servicePeriods.length) {
                availabilitySchedules[i][diningOption].startTime = moment.parseZone(servicePeriods[0].startTime).format('HH:mm:00.000');
                availabilitySchedules[i][diningOption].endTime = moment.parseZone(servicePeriods[servicePeriods.length - 1].endTime).format('HH:mm:00.00');
                availabilitySchedules[i].isOpen = true;
            } else {
                availabilitySchedules[i][diningOption].startTime = null;
                availabilitySchedules[i][diningOption].endTime = null;
                availabilitySchedules[i].isOpen = availabilitySchedules[i].isOpen || false;
            }
        }
    }

    var getAvailabilitySchedules = function() {
        return $q.all([
            formatOOSchedule(ORDER_TYPES.DELIVERY),
            formatOOSchedule(ORDER_TYPES.TAKE_OUT)
        ]).then(function([delivery, takeout]) {
            var sortedSchedule = { delivery, takeout };
            availabilitySchedules = [];
            for (var i = 0; i < DAYSOFWEEK.length; i += 1) {
                availabilitySchedules.push({
                    day: DAYSOFWEEK[i],
                    isOpen: false,
                    takeout: {
                        startTime: '',
                        endTime: ''
                    },
                    delivery: {
                        startTime: '',
                        endTime: ''
                    }
                });
            }
            _setAvailabilitySchedules(sortedSchedule.takeout, 'takeout', availabilitySchedules);
            _setAvailabilitySchedules(sortedSchedule.delivery, 'delivery', availabilitySchedules);
            return availabilitySchedules;

        });
    };

    var getAvailableHoursOfCurrentDay = function() {
        return getAvailableHoursFromTime(getInitialDateTime());
    };

    var getAvailableHoursFromTime = function(time) {
        var weekDay = DAYSOFWEEK[moment(time).isoWeekday() - 1]; // weekday string
        return getAvailabilitySchedules().then(function(availabilitySchedules) {
            for (var i = 0; i < availabilitySchedules.length; i += 1) {
                if (availabilitySchedules[i].day === weekDay) {
                    return availabilitySchedules[i];
                }
            }

            return null;
        });

    };

    var timeConstructor = function(timeString){
        var tempSplit = timeString.split(/\s|:/);
        return {
            hours: parseInt(tempSplit[0]),
            minutes: parseInt(tempSplit[1]),
            extra: tempSplit[2]
        };
    };

    /**
     * @param {string} diningOption
     * @param {moment.Moment} day
     * @returns {Promise<boolean>}
     */
    var isRestaurantOpenOnDay = function(diningOption, day) {
        return $q.all([
            formatOOSchedule(diningOption),
            _formatOOScheduleOverrides(day.clone(), diningOption),
        ]).then(([servicePeriodsByDayOfWeek, overrideServicePeriods]) => {
            let businessDayServicePeriods = overrideServicePeriods;

            if (null === businessDayServicePeriods && angular.isArray(servicePeriodsByDayOfWeek[day.isoWeekday() - 1])) {
                businessDayServicePeriods = servicePeriodsByDayOfWeek[day.isoWeekday() - 1];
            }

            return angular.isArray(businessDayServicePeriods) && businessDayServicePeriods.length > 0;
        });
    };

    /**
     *
     * @param {*} day
     * @param {*} diningOption
     * @returns {Promise<boolean>}
     */
    function isBeforeOpen(day, diningOption) {
        return getStartEndTimes(day, diningOption).then(function(startEndTimes) {
            for (var i = 0; i < startEndTimes.length; i++) {
                if (day.isBefore(startEndTimes[i].startTime)) {
                    return true;
                }
            }
            return false;
        }).catch(() => false);
    }

    /**
     *
     * @param {*} diningOption
     * @param {*} day
     * @returns {Promise<boolean>}
     */
    function isRestaurantOpenAtTime(diningOption, day) {
        return isRestaurantOpenOnDay(diningOption, day).then(function(isOpen) {
            if(isOpen) {
                return getStartEndTimes(day, diningOption).then(function(servicePeriods) {
                    return withinServicePeriodInclusive(day, servicePeriods);
                });
            }
            return false;
        });
    }

    /**
     * Returns whether or not the restaurant is currently accepting orders today.
     * Only called for ASAP orders.
     *
     * @param {string} diningOption
     * @returns {Promise<boolean>}
     */
    var getRestaurantAvailability = function(diningOption) {
        var deferred = $q.defer(),
            current = moment.parseZone(getInitialDateTime());
        // If restaurant is closed today and bein in this function implies ASAP only => return false
        isRestaurantOpenOnDay(diningOption, current).then(function(isOpen) {
            if(!isOpen) {
                deferred.resolve(false);
                return;
            }

            getStartEndTimes(current, diningOption).then(function(times) {
                // The resolver boolean checks if current (time) falls between any orderable period
                var resolver = withinServicePeriodInclusive(current, times);
                deferred.resolve(resolver);
            }).catch(function(error) {
                deferred.reject(error);
            });
        }).catch(function(error) {
            deferred.reject(error);
        });

        return deferred.promise;
    };

    var withinPeriodInclusive = function(date, period){
        return date.isBetween(period.startTime, period.endTime, 'minutes')
               || date.isSame(period.startTime, 'minutes') || date.isSame(period.endTime, 'minutes');
    };

    function withinServicePeriodInclusive(date, servicePeriods) {
        var within = false;
        servicePeriods.forEach(function(elem){
            within = within || withinPeriodInclusive(date, elem);
        });
        return within;
    }

    /**
     * @param {moment.Moment} date, the date in which you want to know what serice period is in
     * @returns {*} false if not within a service period and the period if it is
     */
    function _servicePeriodDateIsIn(date, servicePeriods) {
        return servicePeriods.find(el => date.isBetween(el.startTime, el.endTime, 'minutes', '[]'));
    }

    /**
     * Determines if the restaurant is open now as well as open at the earliest fulfillment
     * time (i.e. now plus prep and throttling).
     *
     * @param {string} diningOption
     * @returns {Promise<boolean>}
     */
    function isOpenNowAndWithPrep(diningOption) {
        const dateSelected = moment.parseZone(getInitialDateTime()).clone();

        return $q.all([
            getStartEndTimes(dateSelected, diningOption)
                .catch(() => $q.resolve([])),
            getStartEndTimes(dateSelected.clone().add(1, 'days'), diningOption)
                .catch(() => $q.resolve([])),
            restaurantInfoFactory.getInfo(['prepTimes'])
        ]).then(
            allResolveds => _nowAndPlusPrepTimeInSameServicePeriod(dateSelected, diningOption, ...allResolveds),
            () => false
        );
    }

    /**
     * Determines if the current time as well as the current time plus
     * prep and throttling are within the same service period.
     *
     * This has to account for service periods that span midnight since the
     * schedule is with respect to business days on the backend but with
     * respect to calendar days on the front end.
     *
     * @returns {boolean}
     */
    function _nowAndPlusPrepTimeInSameServicePeriod(dateSelected, diningOption, startEndTimes, tomorrowStartEndTimes, info) {
        const prepTimes = info.prepTimes;
        const todayPlusPrep = dateSelected.clone();
        addPrepAndThrottlingTime(todayPlusPrep, diningOption, prepTimes);

        // Note this is un-doing how _addServicePeriods() splits service periods across midnight, which
        // sets today's last period to end at 23:59 and tomorrow's first period to start at 00:00, in
        // order to go back to the business-day schedule from the calendar-day based schedule.

        // If today's last period ends at midnight and tomorrow's first period starts at midnight...
        if (
            startEndTimes.length
            && tomorrowStartEndTimes.length
            && startEndTimes[startEndTimes.length - 1].endTime.format('HH:mm') === '23:59'
            && tomorrowStartEndTimes[0].startTime.format('HH:mm') === '00:00'
        ) {
            // Make today's last period extend through the end of tomorrow's first period.
            startEndTimes[startEndTimes.length - 1].endTime = tomorrowStartEndTimes[0].endTime;
        }

        const currentServicePeriod = _servicePeriodDateIsIn(dateSelected, startEndTimes);
        const plusPrepTimeServicePeriod = _servicePeriodDateIsIn(todayPlusPrep, startEndTimes);

        if (!currentServicePeriod) {
            return false;
        }

        return currentServicePeriod === plusPrepTimeServicePeriod;
    }


    /**
     *
     * @param {moment.Moment} momentDate the moment to which prep/throttling time should be added
     * @param {string} diningOption
     * @param {*} prepTimes prep time info object from the server
     * @returns {moment.Moment} the potentially modified momentDate that was passed in
     * @deprecated renamed to `addPrepAndThrottlingTime()` for clarity
     */
    function addTimes(momentDate, diningOption, prepTimes) {
        return addPrepAndThrottlingTime(momentDate, diningOption, prepTimes);
    }

    /**
     *
     * @param {moment.Moment} momentDate the moment to which prep and throttling time should be added
     * @param {string} diningOption
     * @param {*} prepTimes prep time info object from the server
     * @returns {moment.Moment} the potentially modified momentDate that was passed in
     */
    function addPrepAndThrottlingTime(momentDate, diningOption, prepTimes) {
        momentDate = addThrottlingTime(momentDate, diningOption, prepTimes);
        momentDate = addPrepTime(momentDate, diningOption, prepTimes);
        return momentDate;
    }

    /**
     *
     * @param {moment.Moment} momentDate the moment to which prep time should be added
     * @param {string} diningOption
     * @param {*} prepTimes prep time info object from the server
     * @returns {moment.Moment} the potentially modified momentDate that was passed in
     */
    function addPrepTime(momentDate, diningOption, prepTimes) {
        const prepTime = diningOption === ORDER_TYPES.DELIVERY ? prepTimes.deliveryPrepTime : prepTimes.takeoutPrepTime;
        momentDate.add(prepTime, 'minutes');
        return momentDate;
    }

    /**
     *
     * @param {moment.Moment} momentDate the moment to which throttling time should be added
     * @param {string} diningOption
     * @param {*} prepTimes prep time info object from the server
     * @returns {moment.Moment} the potentially modified momentDate that was passed in
     */
    function addThrottlingTime(momentDate, diningOption, prepTimes) {
        const throttleTime = diningOption === ORDER_TYPES.DELIVERY ? prepTimes.deliveryThrottlingTime : prepTimes.takeoutThrottlingTime;
        const enableThrottling = prepTimes.onlineOrderThrottlingEnabled;

        if (enableThrottling) {
            momentDate.add(throttleTime, 'minutes');
        }

        return momentDate;
    }

    /**
     * @param {moment.Moment} time
     * @param {Array} servicePeriods array of service period objects
     * @returns {moment.Moment}
     */
    function _nextPeriod(time, servicePeriods) {
        var nextTime = servicePeriods[servicePeriods.length - 1].startTime;

        for (var i = 0; i < servicePeriods.length; i++) {
            var period = servicePeriods[i];
            if(time.isBetween(period.startTime, period.endTime)) {
                return time;
            } else if (time.isBefore(period.startTime)) {
                return period.startTime;
            }
        }
        return nextTime;
    }

    var getOrderTimes = function(dateSelected, diningOption){
        var deferred = $q.defer();
        var listOfTimes = [];
        var today,
            todayPlusPrep,
            earliestFulfillmentTime,
            date,
            prepTimes,
            prepTime;

        restaurantInfoFactory.getInfo(['prepTimes']).then(function(info){
            today = moment.parseZone(getInitialDateTime());
            date = moment(dateSelected).utcOffset(getInitialDateTime());
            prepTimes = info.prepTimes;
            prepTime = diningOption === ORDER_TYPES.DELIVERY ? prepTimes.deliveryPrepTime : prepTime = prepTimes.takeoutPrepTime;
            todayPlusPrep = today.clone();
            addTimes(todayPlusPrep, diningOption, prepTimes);
            earliestFulfillmentTime = addPrepAndThrottlingTime(
                today.clone(), diningOption, info.prepTimes
            );
            return getStartEndTimes(date, diningOption);
        }).then(function(startEndTimes) {
            var startPeriod = startEndTimes[0],
                endPeriod = startEndTimes[startEndTimes.length - 1];
            if(_servicePeriodDateIsIn(today, startEndTimes) === _servicePeriodDateIsIn(todayPlusPrep, startEndTimes) &&
                    !!_servicePeriodDateIsIn(today, startEndTimes)){
                listOfTimes.push('ASAP');
                _computeTimes(endPeriod.endTime, todayPlusPrep, listOfTimes, startEndTimes, prepTime, earliestFulfillmentTime);
            } else if(today.isSame(date, 'day') && today.isBefore(startPeriod.startTime, 'minutes')){
                addTimes(today, diningOption, prepTimes);
                if(today.isAfter(startPeriod.startTime)){
                    _computeTimes(endPeriod.endTime, moment.parseZone(today), listOfTimes, startEndTimes, prepTime, earliestFulfillmentTime);
                }
                else{
                    _computeTimes(endPeriod.endTime, moment.parseZone(startPeriod.startTime), listOfTimes, startEndTimes, prepTime, earliestFulfillmentTime);
                }
            } else if(today.isSame(date, 'day') && today.isBefore(endPeriod.startTime, 'minutes')){
                addTimes(today, diningOption, prepTimes);
                if(today.isAfter(endPeriod.startTime)){
                    _computeTimes(endPeriod.endTime, moment.parseZone(today), listOfTimes, startEndTimes, prepTime, earliestFulfillmentTime);
                }
                else{
                    var nextStart = _nextPeriod(today, startEndTimes);
                    _computeTimes(endPeriod.endTime, moment.parseZone(nextStart), listOfTimes, startEndTimes, prepTime, earliestFulfillmentTime);
                }
            } else if(moment(today).add(1, 'day').diff(date, 'day') == 0 && startPeriod.startTime.hours() === 0
                        && moment(today).add(prepTime, 'minutes').hours() < CLOSE_OUT){
                // case when ordering for overnight hours before midnight and current time + prep time
                // goes into next day AND is before closeout hour
                listOfTimes.push('ASAP');
                addTimes(today, diningOption, prepTimes);
                _computeTimes(endPeriod.endTime, today, listOfTimes, startEndTimes, prepTime, earliestFulfillmentTime);
            } else if (today.isSame(date, 'day') && date.isAfter(endPeriod.endTime)) {
                _computeTimes(endPeriod.endTime, today, listOfTimes, startEndTimes, prepTime, earliestFulfillmentTime);
            } else {
                date.hours(startPeriod.startTime.hours());
                date.minutes(startPeriod.startTime.minutes());
                _computeTimes(endPeriod.endTime, moment.parseZone(startPeriod.startTime), listOfTimes, startEndTimes, prepTime, earliestFulfillmentTime);
            }

            deferred.resolve(listOfTimes);
        }).catch(function(error) {
            deferred.reject(error);
        });

        return deferred.promise;
    };

    /**
     *
     * @param {moment.Moment} endTimeAfterOffset
     * @param {moment.Moment} date
     * @param {string[]} listOfTimes output where times will be appended
     * @param {any[]} servicePeriods
     * @param {number} prepTime
     * @param {moment.Moment} earliestFulfillmentTime
     * @returns {void}
     */
    function _computeTimes(endTimeAfterOffset, date, listOfTimes, servicePeriods, prepTime, earliestFulfillmentTime) {
        if (date.minutes() % DISCRETE_TIME_INTERVAL !== 0) {
            let diff = DISCRETE_TIME_INTERVAL - date.minutes() % DISCRETE_TIME_INTERVAL;
            date.add(diff, 'minutes');
        }
        else if (listOfTimes[0] === 'ASAP') {
            date.add(15, 'minutes');
        }

        servicePeriods.forEach(function(elem, index, array) {
            let startTime = elem.startTime.clone();

            // Don't add the prep time if the current service period is where the hours
            // were split because the schedule crossed midnight.
            if (!(index === 0 && servicePeriods[index].name === 'previous')) {
                startTime.add(prepTime, 'minutes');
            }

            if (startTime.minutes() % DISCRETE_TIME_INTERVAL !== 0) {
                let diff = DISCRETE_TIME_INTERVAL - startTime.minutes() % DISCRETE_TIME_INTERVAL;
                startTime.add(diff, 'minutes');
            }

            let currentServicePeriodHadTimes = false;

            if (date.isBefore(startTime)) {
                date = moment.parseZone(startTime);
            }

            while (
                date.isBetween(startTime, elem.endTime, 'minutes', '[]') &&
                date.isSameOrBefore(endTimeAfterOffset, 'minutes') &&
                startTime.isSameOrBefore(elem.endTime)
            ) {
                if (!date.isBefore(earliestFulfillmentTime)) {
                    listOfTimes.push(date.format('hh:mm A'));
                    currentServicePeriodHadTimes = true;
                }

                date.add(DISCRETE_TIME_INTERVAL, 'minutes');
            }

            if (currentServicePeriodHadTimes) {
                if (index === 0 && servicePeriods[index].name === 'previous') {
                    let temp = moment.parseZone(date);
                    listOfTimes.push('The above hours are from ' + temp.subtract(1, 'days').format('ddd, MMM D'));
                }
                else if (index === array.length - 1 && servicePeriods[array.length - 1].overnight) {
                    listOfTimes.push('Hours after 11:59 PM are listed on ' + date.format('ddd, MMM D'));
                }
                else if (index !== array.length - 1) {
                    listOfTimes.push(SERVICE_DIVIDER);
                }
            }
        });

        if (!listOfTimes.length) {
            listOfTimes.push(SERVICE_DIVIDER);
        }
    }

    /**
     * Returns an array of arrays with the first level corresponding to the dayOfWeek with
     * 0=Monday and 6=Sunday (i.e. ISO minus 1). Each nested array is the list of service period objects for that
     * business day.
     *
     * @param {String} diningOption
     * @returns {Promise<any[][]>}
     */
    function formatOOSchedule(diningOption) {
        return restaurantInfoFactory.getInfo(['schedules']).then(info => {
            let servicePeriodsForDiningOption = info.ooSchedule.dayServicePeriods.filter(el =>
                el.diningOptionBehavior === diningOption);
            let allDays = [];

            for (let i = 0; i <= 6; i++) {
                allDays[i] = servicePeriodsForDiningOption
                    .filter(el => el.dayOfWeek == (i + 1))
                    .sort((a, b) => a.startTime >= b.startTime)
                    .map(el => Object.assign({},
                        el,
                        {
                            overnight: false,
                            override: false,
                            serviceName: 'current'
                        }
                    ));
            }

            return allDays;
        });
    }

    /**
     * Returns an array of service period objects for the given business date and dining option.
     * null is returned if there is no override, indicating the regular day schedule should be used.
     * Note that an empty array means that there is an override dictating being closed that date.
     *
     * @param {moment.Moment|string} date business date
     * @param {string} diningOption
     * @returns {Promise<any[]>}
     */
    function _formatOOScheduleOverrides(date, diningOption) {
        let dateString = moment.isMoment(date) ? date.format('YYYY-MM-DD') : date;

        return restaurantInfoFactory.getInfo(['schedules']).then(info => {
            let dateOverride = info.ooScheduleOverrides.dateOverrides.find(el =>
                el.overrideDate === dateString
                && el.diningOptionBehaviors.includes(diningOption)
            );

            if (!dateOverride) {
                return null;
            }

            let servicePeriods = dateOverride.dateServicePeriods.map(el => Object.assign({},
                el,
                {
                    overnight: false,
                    override: true,
                    serviceName: 'current'
                }
            )).sort((a, b) => a.startTime >= b.startTime);

            return servicePeriods;
        });
    }

    /**
     * Adds service periods to the supplied array from the given schedule.  The given schedule will be split
     * at midnight as necessary to convert between business day and calendar day.
     *
     * @param {Array} schedule array of service period objects
     * @param {*} dateSelected calendar date
     * @param {Array} servicePeriods the array into which the service periods should be added
     * @param {boolean} isDayBefore
     */
    function _addServicePeriods(schedule, dateSelected, servicePeriods, isDayBefore) {
        for (var i = 0; i < schedule.length; i++){
            let startTime = timeConstructor(schedule[i].startTime),
                tempStartTime = moment.parseZone(dateSelected);
            tempStartTime.set({ hour: startTime.hours, minute: startTime.minutes });

            var endTime = timeConstructor(schedule[i].endTime),
                tempEndTime = moment.parseZone(dateSelected);
            tempEndTime.set({ hours: endTime.hours, minutes: endTime.minutes });

            if (tempEndTime.isBefore(tempStartTime, 'minutes')) {
                tempEndTime.set({ hours: 23, minutes: 59 });
                schedule[i].overnight = true;

                if (isDayBefore) {
                    var newServicePeriodStart = moment.parseZone(dateSelected);
                    schedule[i].overnight = false;
                    newServicePeriodStart.set({ hours: 0, minutes: 0 });
                    var newServicePeriodEnd = moment.parseZone(dateSelected);
                    newServicePeriodEnd.set({ hours: endTime.hours, minutes: endTime.minutes });
                    servicePeriods.unshift({
                        startTime: newServicePeriodStart,
                        endTime: newServicePeriodEnd,
                        overnight: false,
                        override: schedule[i].override,
                        name: 'previous'
                    });
                    return;
                }
            }

            if (!isDayBefore) {
                servicePeriods.push({
                    startTime: tempStartTime,
                    endTime: tempEndTime,
                    overnight: schedule[i].overnight,
                    override: schedule[i].override,
                    name: schedule[i].serviceName
                });
            }
        }
    }

    /**
     * Given the necessary business day schedules, convert them into service periods that are bounded by midnight
     * in order to correspond with the calendar day instead of the business day.
     *
     * @param {Array} dayBeforeBusinessDayServicePeriods array of service period objects for the business day before dateSelected
     * @param {Array} businessDayServicePeriods array of service period objects for the business day that corresponds to dateSelected
     * @param {*} dateSelected calendar date
     * @returns {Array} array of service period objects for the given calendar day
     */
    function _scheduleToServicePeriod(dayBeforeBusinessDayServicePeriods, businessDayServicePeriods, dateSelected) {
        let servicePeriods = [];
        _addServicePeriods(businessDayServicePeriods, dateSelected, servicePeriods, false);
        _addServicePeriods(dayBeforeBusinessDayServicePeriods, dateSelected, servicePeriods, true);
        return servicePeriods;
    }

    /**
     * Returns an array of service period objects for the given calendar date, accounting for overrides
     * as necessary. Note that each service period's start/end time represents first fire and
     * last fulfillment time respectively.
     *
     * @param {moment.Moment|string} dateSelected calendar date
     * @param {string} diningOption
     * @returns {Promise<any[]>} will resolve with a new array
     */
    function getStartEndTimes(dateSelected, diningOption){
        let date = moment.parseZone(dateSelected);

        if (!diningOption) {
            return $q.reject('No valid dining options have been selected.');
        }

        let deferred = $q.defer();

        $q.all([
            formatOOSchedule(diningOption),
            _formatOOScheduleOverrides(date.clone(), diningOption),
            _formatOOScheduleOverrides(date.clone().subtract(1, 'day'), diningOption)
        ]).then(([servicePeriodsByDayOfWeek, overrideServicePeriods, dayBeforeOverrideServicePeriods]) => {
            let businessDayServicePeriods = (null !== overrideServicePeriods)
                    ? overrideServicePeriods
                    : servicePeriodsByDayOfWeek[(date.day() - 1 + 14) % 7],
                dayBeforeBusinessDayServicePeriods = (null !== dayBeforeOverrideServicePeriods)
                    ? dayBeforeOverrideServicePeriods
                    : servicePeriodsByDayOfWeek[(date.day() + 14 - 2) % 7],
                calendarDayServicePeriods = _scheduleToServicePeriod(dayBeforeBusinessDayServicePeriods, businessDayServicePeriods, dateSelected);

            if (calendarDayServicePeriods.length) {
                deferred.resolve(calendarDayServicePeriods);
            } else {
                deferred.reject('No available times for selected date.');
            }
        });

        return deferred.promise;
    }

    /*
        rounds selected time (i.e. "1:03 PM") to closest discrete interval
    */
    var roundTime = function(timeString){
        if(timeString === 'ASAP'){
            return timeString;
        }
        else{
            var time = timeConstructor(timeString);

            if(time.minutes % DISCRETE_TIME_INTERVAL === 0){
                return timeString;
            }
            else if(time.hours === 11 && time.minutes > 45 && time.extra === 'AM'){
                return '12:00 PM';
            }
            else if(time.hours === 11 && time.minutes > 45 && time.extra === 'PM'){
                return '12:00 AM';
            }
            else if(time.minutes > 45){
                return time.hours + 1 + ':00 ' + time.extra;
            }
            else{
                time.minutes += (DISCRETE_TIME_INTERVAL - time.minutes % DISCRETE_TIME_INTERVAL);
                return time.hours + ':' + time.minutes + ' ' + time.extra;
            }
        }
    };

    return {
        getRestaurantAvailability: getRestaurantAvailability,
        getStartEndTimes: getStartEndTimes,
        getOrderTimes: getOrderTimes,
        roundTime: roundTime,
        setInitialDateTime: setInitialDateTime,
        getInitialDateTime: getInitialDateTime,
        setTimeZone: setTimeZone,
        getTimeZone: getTimeZone,
        getOperationHours: getOperationHours,
        getAvailabilitySchedules: getAvailabilitySchedules,
        getAvailableHoursOfCurrentDay: getAvailableHoursOfCurrentDay,
        getAvailableHoursFromTime: getAvailableHoursFromTime,
        addTimes,
        addPrepAndThrottlingTime,
        addPrepTime,
        addThrottlingTime,
        isRestaurantOpenAtTime: isRestaurantOpenAtTime,
        isRestaurantOpenOnDay: isRestaurantOpenOnDay,
        formatOOSchedule: formatOOSchedule,
        isBeforeOpen: isBeforeOpen,
        isOpenNowAndWithPrep: isOpenNowAndWithPrep
    };
}
